<template>
  <BaseDialog centered contentClass="sync-quote" ref="dialog" :show-actions="true" persistent>
    <template slot="title"
      >{{ $t('quotes.synchronize-quote') }}
      <div class="sync-quote__subtitle">{{ $t('basic-words.to-2') }} {{ syncProfileName }}</div>
    </template>
    <template slot="content" v-if="quote">
      <form @click.prevent @keydown.enter.prevent>
        <div class="sync-opportunity__external-fields" v-if="hasFieldMappings && displayableFieldMappings.length > 0">
          <div class="sync-opportunity__external-fields--subtitle">
            {{ $t('page.opportunity-details.sync-validation.sync-details') }}
          </div>
          <SyncFieldMappings
            :key="fieldMappingsKey"
            :field-mappings="quoteFieldMappings"
            :values="fieldValues"
            :sync-profile-id="syncProfile.id"
            :sync-profile="syncProfile"
            :is-create="isCreate"
            :default-queries="defaultQueries"
            :create-defaults="createDefaults"
            @input="fieldValues = $event"
          />
        </div>
        <div class="sync-opportunity__actions">
          <FormButton is-outlined class="sync-opportunity__cancel" @click.native="cancelClick">{{
            $t('actions.cancel')
          }}</FormButton>
          <FormButton @click.native="onSubmit" type="submit">
            <span>{{ $t('actions.confirm') }}</span>
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import FormButton from '@/components/form/FormButton';
import SyncFieldMappings from '@/components/sync/SyncFieldMappings';

import { buildLookupFieldDefaults, filterFieldMappingsForMode, supportsFieldMappings } from '@/util/sync-profile-util';
import { formatPhoneNumber } from '@/util/phone-util';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'SyncQuoteDialog',
  components: {
    BaseDialog,
    FormButton,
    SyncFieldMappings,
  },
  data() {
    return {
      quote: null,
      quoteSync: null,
      syncProfile: null,
      fieldValues: {},
      defaultQueries: {},
      createDefaults: {},
      fieldMappingsKey: 0,
    };
  },
  computed: {
    ...mapGetters('syncFieldMappings', { syncFieldMappingsList: 'fieldMappings' }),
    isCreate() {
      // A quote already in the CRM (externalQuoteId set) is an update, even after a failed retry.
      return this.quoteSync ? this.quoteSync.externalQuoteId == null : true;
    },
    quoteFieldMappings() {
      return [...this.syncFieldMappingsList].sort((a, b) => a.displayOrder - b.displayOrder);
    },
    displayableFieldMappings() {
      return filterFieldMappingsForMode(this.quoteFieldMappings, this.isCreate);
    },
    hasFieldMappings() {
      return this.syncProfile && supportsFieldMappings(this.syncProfile.type);
    },
    syncProfileName() {
      return this.syncProfile ? this.syncProfile.name : '';
    },
  },
  methods: {
    ...mapActions('syncFieldMappings', ['getFieldMappings']),
    async openDialog(quote, quoteSync, syncProfile) {
      this.quote = quote;
      this.quoteSync = quoteSync;
      this.syncProfile = syncProfile;
      this.fieldValues = {};
      this.defaultQueries = {};
      this.createDefaults = {};
      this.fieldMappingsKey++;
      this.$refs.dialog.openModal();

      if (this.hasFieldMappings) {
        const fieldMappings = await this.getFieldMappings({
          syncProfileId: this.syncProfile.id,
          scope: 'quote',
          formOnly: true,
        });
        this.applyLookupDefaults(fieldMappings);
      }
    },
    applyLookupDefaults(fieldMappings) {
      const contactName = [this.quote.contactFirstName, this.quote.contactLastName].filter(Boolean).join(' ');
      const { defaultQueries, createDefaults } = buildLookupFieldDefaults(fieldMappings, {
        accountQuery: this.quote.accountName || '',
        accountCreate: { name: this.quote.accountName || '' },
        contactQuery: contactName,
        contactCreate: {
          firstName: this.quote.contactFirstName || '',
          lastName: this.quote.contactLastName || '',
          email: this.quote.contactEmail || '',
          phone: this.quote.contactPhone ? formatPhoneNumber(this.quote.contactPhone) : '',
        },
      });
      this.defaultQueries = defaultQueries;
      this.createDefaults = createDefaults;
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
    },
    onSubmit() {
      this.$emit('confirm', this.quote, this.syncProfile, this.fieldValues);
      this.closeDialog();
    },
    cancelClick() {
      this.closeDialog();
    },
  },
};
</script>

<style lang="scss">
.base-dialog__sync-quote {
  height: auto;
  width: 750px;
  position: relative;

  .base-dialog__content {
    padding: 32px;
    font-size: 18px;
    background: $color-white !important;
  }

  .close-icon__container {
    display: none;
  }
}

.sync-quote {
  &__subtitle {
    font-size: 16px;
    font-family: 'Lato';
    color: $color-normal-pale;
    font-weight: normal;
    text-transform: capitalize;
  }
}
</style>
